/** ER24 emergency short code dialed by the Contact ER24 button. */
export const ER24_CONTACT_NUMBER = "084124";

const ENV_KEYS = {
  socketUrl: ["VITE_SOCKET_URL"],
  operatorWsUrl: ["VITE_OPERATOR_WS_URL"],
  sidecarApiUrl: ["VITE_SIDECAR_API_URL"],
  operatorMaxConcurrentCalls: ["VITE_OPERATOR_MAX_CONCURRENT_CALLS"],
  sipWssUrl: ["VITE_SIP_WSS_URL"],
  sipDomain: ["VITE_SIP_DOMAIN"],
  sipAudioCodecs: ["VITE_SIP_AUDIO_CODECS"],
  sipIceServers: ["VITE_SIP_ICE_SERVERS"],
} as const;

function readEnv(keys: readonly string[]) {
  for (const key of keys) {
    const value = (import.meta.env as Record<string, string | undefined>)[key];
    if (value) return value;
  }
  return undefined;
}

export function resolveSocketUrl() {
  const url = readEnv(ENV_KEYS.socketUrl);
  if (!url) {
    console.error(
      "Missing Socket.IO URL. Set VITE_SOCKET_URL."
    );
    return null;
  }
  return url;
}

/** Base URL for sidecar REST API (POST /call-answered, POST /call-ended). Defaults to same host as socket, port 3443. */
export function resolveSidecarApiUrl() {
  const configured = readEnv(ENV_KEYS.sidecarApiUrl);
  if (configured) return configured;
  const socketUrl = resolveSocketUrl();
  if (!socketUrl) return null;
  try {
    const u = new URL(socketUrl);
    const shouldUseTls = u.protocol === "https:" || u.protocol === "wss:";

    u.protocol = shouldUseTls ? "https:" : "http:";
    u.port = "3443";
    u.pathname = "/";
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    return null;
  }
}

export function resolveOperatorWsUrl(socketUrl?: string | null) {
  const configured = readEnv(ENV_KEYS.operatorWsUrl);
  if (configured) return configured;

  const source = socketUrl ?? resolveSocketUrl();
  if (!source) {
    console.warn(
      "Missing operator WS URL. Set VITE_OPERATOR_WS_URL."
    );
    return null;
  }

  try {
    const parsed = new URL(source);
    const shouldUseTls =
      parsed.protocol === "https:" || parsed.protocol === "wss:";
    parsed.protocol = shouldUseTls ? "wss:" : "ws:";
    parsed.port = "8080";
    parsed.pathname = "/";
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    console.warn(
      "Invalid socket URL. Unable to derive operator WS URL; set VITE_OPERATOR_WS_URL.",
      { socketUrl: source }
    );
    return null;
  }
}

export function resolveOperatorMaxConcurrentCalls(defaultValue = 2) {
  const raw = readEnv(ENV_KEYS.operatorMaxConcurrentCalls);
  if (!raw) return defaultValue;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

export function resolveSipWssUrl() {
  const url = readEnv(ENV_KEYS.sipWssUrl);
  if (!url) {
    console.error(
      "Missing SIP WSS URL. Set VITE_SIP_WSS_URL."
    );
    return null;
  }
  return url;
}

const isIpAddress = (value: string) => {
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(value)) return true;
  return value.includes(":") && value.includes(".");
};

const readWssHostname = (sipWssUrl?: string | null) => {
  if (!sipWssUrl) return null;
  try {
    return new URL(sipWssUrl).hostname || null;
  } catch {
    return null;
  }
};

/**
 * SIP registration URI domain/realm. When VITE_SIP_DOMAIN is a bare IP but the WSS
 * edge uses a TLS hostname, register with the WSS hostname so REGISTER matches
 * what Asterisk expects on the Apache-terminated WebSocket path.
 */
export function resolveSipRegistrationDomain(sipWssUrl?: string | null) {
  const configured = readEnv(ENV_KEYS.sipDomain);
  const wssHost = readWssHostname(sipWssUrl);

  if (configured && wssHost && isIpAddress(configured) && !isIpAddress(wssHost)) {
    console.warn(
      `VITE_SIP_DOMAIN is ${configured} but WSS host is ${wssHost}; using ${wssHost} for SIP REGISTER URI.`
    );
    return wssHost;
  }

  return resolveSipDomain(sipWssUrl);
}

export function resolveSipDomain(sipWssUrl?: string | null) {
  const domain = readEnv(ENV_KEYS.sipDomain);
  if (domain) return domain;

  if (sipWssUrl) {
    try {
      const parsed = new URL(sipWssUrl);
      console.warn(
        "Missing SIP domain/realm. Set VITE_SIP_DOMAIN. Using SIP WSS host as fallback."
      );
      return parsed.hostname;
    } catch {
      // Ignore and warn below.
    }
  }

  console.warn(
    "Missing SIP domain/realm. Set VITE_SIP_DOMAIN."
  );
  return null;
}

export function resolveSipAudioCodecs() {
  const value = readEnv(ENV_KEYS.sipAudioCodecs);
  if (!value) return null;
  const codecs = value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  return codecs.length > 0 ? codecs : null;
}

/**
 * Optional ICE server configuration for SIP/WebRTC media.
 * Supported formats:
 * - JSON array of RTCIceServer objects
 * - Comma-separated STUN/TURN URLs (e.g. "stun:host,turn:host")
 */
export function resolveSipIceServers() {
  const value = readEnv(ENV_KEYS.sipIceServers);
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed as RTCIceServer[];
      }
      console.warn(
        "VITE_SIP_ICE_SERVERS JSON must be an array of RTCIceServer objects."
      );
      return null;
    } catch {
      console.warn(
        "Invalid VITE_SIP_ICE_SERVERS JSON. Ignoring ICE override.",
        { value: trimmed }
      );
      return null;
    }
  }

  const urls = trimmed
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (urls.length === 0) return null;
  return [{ urls }];
}

const encodeSipUserPart = (user: string) => {
  return user.replace(/\*/g, "%2A").replace(/#/g, "%23");
};

export function buildSipUri(target: string, sipDomain: string) {
  if (target.startsWith("sip:")) return target;
  if (target.includes("@")) {
    const [user, host] = target.split("@", 2);
    return `sip:${encodeSipUserPart(user)}@${host}`;
  }
  return `sip:${encodeSipUserPart(target)}@${sipDomain}`;
}

export function deriveConferenceRoom(callUuid?: string | null) {
  const base = 900;
  if (!callUuid) return String(base);
  let hash = 0;
  for (let i = 0; i < callUuid.length; i += 1) {
    hash = (hash * 31 + callUuid.charCodeAt(i)) >>> 0;
  }
  return String(base + (hash % 100));
}
